import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search } from "lucide-react";

const emptyForm = () => ({ fecha: new Date().toISOString().slice(0, 10), referencia: "", tarifa: "", notas: "" });

export default function ReferenciasCummins() {
  const { can } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/referencias-cummins", { params: { search } });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => { setForm(emptyForm()); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/referencias-cummins", { ...form, tarifa: parseFloat(form.tarifa || 0) });
      toast.success("Referencia creada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Referencias Cummins"
        subtitle="Fecha, referencia (TIMP-xxxxx / TEXP-xxxxx) y tarifa — informativo, aparece junto al load que coincida"
        actions={
          can("referencias_cummins", "crear") && (
            <Button onClick={openCreate} data-testid="new-referencia-cummins-btn" className="gap-2">
              <Plus className="h-4 w-4" /> Nueva referencia
            </Button>
          )
        }
      />

      <div className="mb-4 max-w-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar por referencia" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="referencia-cummins-search" />
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Referencia</TableHead>
              <TableHead className="text-right">Tarifa</TableHead>
              <TableHead>Notas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((r) => (
              <TableRow key={r.id} data-testid={`referencia-cummins-row-${r.id}`}>
                <TableCell className="text-xs">{r.fecha}</TableCell>
                <TableCell className="font-mono text-xs font-medium">{r.referencia}</TableCell>
                <TableCell className="text-right font-mono text-xs">${Number(r.tarifa).toLocaleString("en-US", { minimumFractionDigits: 2 })}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.notas || "—"}</TableCell>
              </TableRow>
            ))}
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground">Sin referencias registradas.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4 text-sm text-muted-foreground">{data.total} referencia(s)</div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Nueva referencia Cummins</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Fecha *</Label>
                <Input type="date" className="mt-1" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="referencia-cummins-form-fecha" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Tarifa *</Label>
                <Input type="number" className="mt-1" value={form.tarifa} onChange={(e) => setForm({ ...form, tarifa: e.target.value })} data-testid="referencia-cummins-form-tarifa" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Referencia * (ej. TIMP-00123 / TEXP-00045)</Label>
              <Input className="mt-1 font-mono" value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} data-testid="referencia-cummins-form-referencia" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Notas</Label>
              <Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="referencia-cummins-form-notas" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.fecha || !form.referencia || !form.tarifa} data-testid="referencia-cummins-save-btn">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
